/**
 * Hotels Dashboard - HOTELS ON TAP
 * 
 * Simplified dashboard with:
 * - Quick overview stats
 * - Property cards with status indicators
 * - Quick actions for common tasks
 * 
 * UX Principles:
 * - Clear visual hierarchy
 * - One-click access to property management
 * - Status-at-a-glance for all properties
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { hotelsAPI, dashboardAPI, incidentAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import CommandBar from '../../components/CommandBar';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  DoorOpen,
  Tag,
  Activity,
  ChevronRight,
  Star,
  ClipboardCheck,
  BarChart3,
  CreditCard,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  Zap,
  Droplets,
  ShieldAlert,
} from 'lucide-react';

// Property status badge
const PropertyStatus = ({ hotel }) => {
  const hasRooms = hotel.total_rooms > 0;
  const hasTags = hotel.rooms_with_tags > 0;
  const hasReviewUrls = hotel.engagement_modes?.tap_engine_urls?.google_reviews_url || 
                        hotel.engagement_modes?.reviews?.google_url;
  
  if (hasRooms && (hasTags || hasReviewUrls)) {
    return (
      <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30 text-xs">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        Active
      </Badge>
    );
  }
  if (hasRooms) {
    return (
      <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs">
        <Clock className="w-3 h-3 mr-1" />
        Setup Needed
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="bg-gray-500/10 text-gray-400 border-gray-500/30 text-xs">
      <AlertCircle className="w-3 h-3 mr-1" />
      New
    </Badge>
  );
};

// Quick stat card
const QuickStat = ({ title, value, icon: Icon, color, onClick }) => (
  <Card 
    className={`bg-card border-border ${onClick ? 'cursor-pointer hover:border-primary/50 transition-colors' : ''}`}
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          <p className="text-xs text-muted-foreground">{title}</p>
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function HotelsDashboard() {
  const { t, i18n } = useTranslation();
  const [hotels, setHotels] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [incidentsToday, setIncidentsToday] = useState(0);
  const { user } = useAuth();
  const { checkTimeBasedPrompt } = useTheme();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [hotelsRes, statsRes] = await Promise.all([
        hotelsAPI.list(),
        dashboardAPI.getHotelStats(),
      ]);
      setHotels(hotelsRes.data);
      setStats(statsRes.data);
      // Fetch incidents today count
      try {
        const incRes = await incidentAPI.getTodayCount();
        setIncidentsToday(incRes.data?.count || 0);
      } catch (e) { /* incidents may not be set up yet */ }
    } catch (error) {
      toast.error(t('hotels.messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
    checkTimeBasedPrompt();
  }, [fetchData, checkTimeBasedPrompt]);

  // Command bar actions
  const commandBarActions = (
    <div className="flex items-center space-x-2">
      <Button
        data-testid="cmd-cards"
        onClick={() => navigate('/standalone/cards')}
        size="sm"
        variant="outline"
        className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
      >
        <CreditCard className="w-4 h-4 mr-2" />
        {i18n.language === 'fr' ? 'Cartes' : 'Cards'}
      </Button>
      <Button
        data-testid="cmd-add-hotel"
        onClick={() => navigate('/hotels/new')}
        size="sm"
        className="bg-primary hover:bg-primary/90"
      >
        <Plus className="w-4 h-4 mr-2" />
        {t('hotels.addHotel')}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Command Bar */}
      <CommandBar
        title="HOTELS ON TAP"
        subtitle={i18n.language === 'fr' ? 'Gérez vos propriétés' : 'Manage your properties'}
        actions={commandBarActions}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <QuickStat
            title={i18n.language === 'fr' ? 'Propriétés' : 'Properties'}
            value={stats?.total_hotels || hotels.length}
            icon={Building2}
            color="#3b82f6"
          />
          <QuickStat
            title={i18n.language === 'fr' ? 'Chambres' : 'Rooms'}
            value={stats?.total_rooms || 0}
            icon={DoorOpen}
            color="#22c55e"
          />
          <QuickStat
            title={i18n.language === 'fr' ? 'Tags Actifs' : 'Active Tags'}
            value={stats?.rooms_with_tags || 0}
            icon={Tag}
            color="#f59e0b"
          />
          <QuickStat
            title={i18n.language === 'fr' ? 'Taps Total' : 'Total Taps'}
            value={stats?.total_taps || 0}
            icon={Activity}
            color="#8b5cf6"
          />
          <QuickStat
            title={i18n.language === 'fr' ? "Incidents Aujourd'hui" : 'Incidents Today'}
            value={incidentsToday}
            icon={ShieldAlert}
            color="#ef4444"
            onClick={() => navigate('/hotels/incidents')}
          />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className="bg-card border-border cursor-pointer hover:border-red-500/50 transition-all group"
            onClick={() => navigate('/hotels/incidents')}
            data-testid="incident-data-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <ShieldAlert className="w-6 h-6 text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-foreground font-medium">
                    {i18n.language === 'fr' ? 'Donn\u00e9es Incidents' : 'Incident Data'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {i18n.language === 'fr' ? 'Analytique et intelligence op\u00e9rationnelle' : 'Operations analytics & intelligence'}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-card border-border cursor-pointer hover:border-green-500/50 transition-all group"
            onClick={() => navigate('/standalone/cards?type=guest_arrival')}
            data-testid="room-tags-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <ClipboardCheck className="w-6 h-6 text-green-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-foreground font-medium">
                    {i18n.language === 'fr' ? 'Tags Arrivée (Sondages)' : 'Room Tags (Surveys)'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {i18n.language === 'fr' ? 'Configurer les tags NFC pour les chambres' : 'Set up NFC tags for guest rooms'}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-card border-border cursor-pointer hover:border-blue-500/50 transition-all group"
            onClick={() => navigate('/standalone/cards?type=restroom')}
            data-testid="restroom-tags-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Droplets className="w-6 h-6 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-foreground font-medium">
                    {i18n.language === 'fr' ? 'Tags Toilettes (Sondages)' : 'Restroom Tags (Surveys)'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {i18n.language === 'fr' ? 'Configurer les tags NFC pour les toilettes' : 'Set up NFC tags for restrooms'}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="bg-card border-border cursor-pointer hover:border-amber-500/50 transition-all group"
            onClick={() => navigate('/standalone/cards?type=tap_engine')}
            data-testid="review-cards-card"
          >
            <CardContent className="p-5">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Star className="w-6 h-6 text-amber-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-foreground font-medium">
                    {i18n.language === 'fr' ? 'Cartes Review Engine' : 'Review Cards'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {i18n.language === 'fr' ? 'Créer des cartes pour Google, TripAdvisor...' : 'Create cards for Google, TripAdvisor...'}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Properties List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-foreground">
              {i18n.language === 'fr' ? 'Vos Propriétés' : 'Your Properties'}
            </h2>
            {hotels.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/hotels/new')}
                className="text-muted-foreground hover:text-foreground"
              >
                <Plus className="w-4 h-4 mr-1" />
                {i18n.language === 'fr' ? 'Ajouter' : 'Add'}
              </Button>
            )}
          </div>

          {hotels.length === 0 ? (
            <Card className="bg-card border-border border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <Building2 className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {i18n.language === 'fr' ? 'Aucune propriété' : 'No properties yet'}
                </h3>
                <p className="text-muted-foreground text-center max-w-sm mb-4">
                  {i18n.language === 'fr' 
                    ? 'Ajoutez votre première propriété pour commencer à collecter des avis et des sondages.'
                    : 'Add your first property to start collecting reviews and guest feedback.'}
                </p>
                <Button
                  data-testid="empty-add-hotel-btn"
                  onClick={() => navigate('/hotels/new')}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {i18n.language === 'fr' ? 'Ajouter une propriété' : 'Add Property'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {hotels.map((hotel) => (
                <Card
                  key={hotel.id}
                  data-testid={`hotel-card-${hotel.id}`}
                  className="bg-card border-border cursor-pointer transition-all duration-200 hover:border-primary/50 hover:shadow-lg group"
                  onClick={() => navigate(`/hotels/${hotel.id}`)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <PropertyStatus hotel={hotel} />
                    </div>
                    <CardTitle className="text-lg text-foreground mt-3 group-hover:text-primary transition-colors flex items-center gap-2">
                      {hotel.name}
                      {hotel.is_demo_org && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30 font-bold tracking-wider" data-testid={`demo-badge-${hotel.id}`}>
                          DEMO
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground mb-3">
                      {hotel.city || hotel.address || (i18n.language === 'fr' ? 'Aucune adresse' : 'No address')}
                    </CardDescription>
                    
                    {/* Quick Stats Row */}
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="flex items-center text-muted-foreground">
                        <DoorOpen className="w-4 h-4 mr-1" />
                        <span>{hotel.total_rooms || 0}</span>
                      </div>
                      <div className="flex items-center text-muted-foreground">
                        <Tag className="w-4 h-4 mr-1" />
                        <span>{hotel.rooms_with_tags || 0}</span>
                      </div>
                      <div className="flex items-center text-muted-foreground">
                        <Activity className="w-4 h-4 mr-1" />
                        <span>{hotel.total_taps || 0}</span>
                      </div>
                    </div>

                    {/* Manage Button */}
                    <div className="mt-4 pt-3 border-t border-border">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {i18n.language === 'fr' ? 'Cliquez pour gérer' : 'Click to manage'}
                        </span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
